import { Component, inject, OnInit } from '@angular/core';
import {
  DocumentSharing,
  DocumentSharingControllerService,
  Negotiation,
  NegotiationControllerService,
  ProfileProviderPF,
  ProfileProviderPfControllerService,
  ProfileProviderPJ,
  ServiceOffering,
  ServiceOfferingControllerService,
  UserRef,
} from '../../core/generated';
import { UserPreferencesCacheService } from '../../shared/services/user-preferences-cache.service';
import { MaterialModule } from '../../shared/material/material.module';
import { DeviceDetectorService } from 'ngx-device-detector';
import { firstValueFrom } from 'rxjs';
import { ProfileProviderPJCacheListService } from '../../shared/services/profile-provider-pj-cache-list.service';
import { Router, ActivatedRoute } from '@angular/router';
import { PlanStatusService } from '../../shared/services/plan-status-cache.service';
import { Feature } from '../../shared/config/package-features.config';
import { EncryptionUtil } from '../../shared/utils/encryption.util';
import { ProfileProviderPFCacheListService } from '../../shared/services/profile-provider-pf-cache-list.service';
import { EnumDescriptionPipe } from '../../shared/pipes/enum-description.pipe';
import { ContainerComponent } from '../../shared/container/container.component';

@Component({
    selector: 'app-dashboard-provider-pf',
    standalone: true,
    templateUrl: './dashboard-provider-pf.component.html',
    imports: [MaterialModule, EnumDescriptionPipe, ContainerComponent],
    styleUrls: ['./dashboard-provider-pf.component.scss']
})
export class DashboardProviderPFComponent implements OnInit {
  profileProviderPFService = inject(ProfileProviderPfControllerService);
  serviceOfferingService = inject(ServiceOfferingControllerService);
  documentSharingService = inject(DocumentSharingControllerService);
  UserPreferencesCacheService = inject(UserPreferencesCacheService);
  deviceService = inject(DeviceDetectorService);
  planStatusService = inject(PlanStatusService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  negotiationService = inject(NegotiationControllerService);
  private readonly profileProviderPFCacheListService = inject(ProfileProviderPFCacheListService);
  private readonly profileProviderPJCacheListService = inject(ProfileProviderPJCacheListService);


  sharedCompany: ProfileProviderPJ | undefined;
  isMobile: any;
  user!: UserRef;
  profileProviderPF!: ProfileProviderPF;
  documentSharing: DocumentSharing[] = [];
  negotiations: any = [];

  serviceOfferingProviderPF: ServiceOffering[] = [];

  // Expor Feature enum para uso no template
  readonly Feature = Feature;

  isLoading = false;

  async ngOnInit() {
    this.isMobile = this.deviceService.isMobile();
    this.user = await firstValueFrom(this.UserPreferencesCacheService.getUser());
    this.profileProviderPF = await firstValueFrom(this.profileProviderPFService
      .findByUserEmail1(this.user.email));

    if (this.profileProviderPF?.cpf) {
      const serviceOfferingProviderPFContent = await firstValueFrom(this.serviceOfferingService
        .findByCnpj(this.profileProviderPF.cpf));
      this.serviceOfferingProviderPF = serviceOfferingProviderPFContent || [];
    }

    this.documentSharing = (await firstValueFrom(this.documentSharingService
      .getGrantedFrom(this.user.cpf!))) || [];

    // Carregar status dos planos
    await this.planStatusService.loadForCurrentUser();

    // Check if URL has ID parameter
    const id = this.route.snapshot.paramMap.get('id');
    if (id && this.route.snapshot.paramMap.get('id') !== String(this.profileProviderPF.id)) {
      // Navigate to the current route with profile ID appended
      this.router.navigate(['/providerPF', this.profileProviderPF.id]);

    }

    await this.loadNegotiations(this.user.cpf!);
  }

  async loadNegotiations(cnpj: string): Promise<void> {
    const response = await firstValueFrom(this.negotiationService.getNegotiationsByParticipant(cnpj));
    this.negotiations = response || [];
  }

  // Métodos de controle de acesso (seguindo padrão do sidebar)
  canAccess(cpfCnpj: string | undefined | null, feature?: Feature | Feature[]): boolean {
    if (!cpfCnpj) return false;
    return this.planStatusService.canAccessFeature(cpfCnpj, feature).hasAccess;
  }

  hasFeatureAccess(cpfCnpj: string | undefined | null, feature: Feature | Feature[]): boolean {
    if (!cpfCnpj) return false;
    return this.planStatusService.hasFeatureAccess(cpfCnpj, feature);
  }

  hasAnyActivePlan(): boolean {
    return this.planStatusService.hasAnyActivePlan();
  }

  encriptCpfcnpj(cpfcnpj: string): any | string {
    return EncryptionUtil.encrypt(cpfcnpj);
  }

  getCompanyDto(cnpj: string): ProfileProviderPJ {
    const pj =
      this.profileProviderPJCacheListService.findProfileProviderPJByCnpj(
        cnpj
      );
    return pj ? pj : { companyName: 'N/A' } as ProfileProviderPJ;
  }

  getProfilePFDto(cpf: string): ProfileProviderPF {
    const profilePF = this.profileProviderPFCacheListService.findProfileProviderPFByCpf(cpf);
    return profilePF ? profilePF : { firstName: 'Usuário não encontrado' } as ProfileProviderPF;
  }
  
  getParticipantDtoName(negotiation: Negotiation): string | undefined {
    const otherCpfCnpj = negotiation.initiatorCpfCnpj === this.user.cpf
      ? negotiation.recipientCpfCnpj
      : negotiation.initiatorCpfCnpj;
    if (otherCpfCnpj) {
      if (otherCpfCnpj.length === 11) {
        const profilePFDto = this.getProfilePFDto(otherCpfCnpj);
        return profilePFDto.name;
      } else if (otherCpfCnpj.length === 14) {
        const companyDto = this.getCompanyDto(otherCpfCnpj);
        return companyDto.companyName;
      }
    }
    return undefined;
  }

}
